import { Injectable } from '@angular/core';
import { AlertController, LoadingController, Loading } from 'ionic-angular';
import { concat } from 'rxjs/observable/concat';
import { AwsProvider } from '../../providers/aws/aws';

@Injectable()
export class UsersProvider {

  users: Array<object>;
  usersWithUserId: Array<object>;

  // variables for table sorting
  userSym: string;
  userField: string;
  
  constructor(public aws: AwsProvider, public loadingCtrl: LoadingController, public alertCtrl: AlertController) {
  }

  /**
   * Load all Cognito users 
   * @param allUsers: if false, only retrieve users who have previously logged in
   * @param ignoreCache: if true, ignore the cache
   */
  populateUsers(ignoreCache: boolean, allUsers:boolean): void {
    let loading: Loading = this.loadingCtrl.create({});
    loading.present();
    if (!ignoreCache) {
      let cache = window.localStorage.getItem('userCache');
      if (cache) {
        cache = JSON.parse(cache);
        let dateTime = (new Date(cache['dateTime'])).getTime();
        let secondDiff: number = (Date.now() - dateTime) / 1000;
        if (secondDiff < (60 * 60 * 24)) {
          this.users = cache['users'];
          loading.dismiss();
          return;
        }
      }
    }
    this.aws.invokeLambda('LambdaGetCognitoUsers', {'allUsers': allUsers})
    .then((result) => {
      if (result['success']) {
        this.users = result['response']['users'];
        for (let user of this.users) {
          if (!user['username']) user['username'] = '';
        }
        let cache = {
          users: this.users,
          dateTime: (new Date()).toISOString()
        };
        window.localStorage.setItem('userCache', JSON.stringify(cache));
      } else {
        let msg = result || 'Error getting cognito users. Please try again.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
      }
      loading.dismiss();
    });
  }
  /**
   * Load all userIds for Cognito users 
   * @param users: cognito users you want full data for
   */
   populateUsersWithUserId(): void {
    let loading: Loading = this.loadingCtrl.create({});
    loading.present();
    this.aws.invokeLambda('LambdaGetCognitoUsers', {'allUsers': true})
    .then((result) => {
      let loading2: Loading = this.loadingCtrl.create({});
      loading2.present();
      this.aws.invokeLambda('SherloqAdminFEGetUserIdFromCognitoId', result['response']['users']).then((result) => {
        if (result['success']) {
          console.log('success',result);
          let response = result['response'];
          let concatArray = [];
          for(let users of response) {
            for(let user of users.CognitoIdToUser) {
              concatArray.push(user);
            }
          }
          this.usersWithUserId = concatArray;
          for (let user of this.users) {
            if (!user['username']) user['username'] = '';
          }
        } else {
          let msg = result['response'] || 'Error getting cognito users. Please try again.';
          let alert = this.alertCtrl.create({
            message: msg,
            buttons: ['Ok'],
            enableBackdropDismiss: false
          });
          alert.present();
        }
        loading2.dismiss();
      });
      loading.dismiss();
    });
  }
  /**
   * Populates user list based on searchbar input of full or partial email
   * @param email: a full or partial email address
   * @return a list of User objects
   */
  search(email: string, allUsers:boolean): any{
    let loading: Loading = this.loadingCtrl.create({});
    loading.present();
    this.aws.invokeLambda('LambdaAdminGetUserByEmail', { 'email': email }).then((result) => {
      if (result['success']) {
        this.users = result['response']['userRecords'];
        for (let user of this.users) {
          if (!user['username']) user['username'] = '';
        }
        let cache = {
          users: this.users,
          dateTime: (new Date()).toISOString()
        };
        window.localStorage.setItem('userCache', JSON.stringify(cache));
      } else {
        let msg = result || 'Error finding users by email. Please try again.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
      }
      loading.dismiss();
    });
  }

  /**
   * Sort table depending on which column was clicked on
   * @param key object key to sort on
   */
  sortUserTable(key) {
    // return if not enough entries
    if (!this.users || this.users.length < 2) {
      return
    }
    // set current sort field to key
    this.userField = key;

    // get current order of column as a string
    let order: String = JSON.stringify(this.users.map(item => item[key]));

    // sort ascendingly
    this.users.sort((a, b) => {
      if (typeof a[key] == 'number') {
        if (a[key] < b[key]) return -1;
        else if (a[key] > b[key]) return 1;
        else return 0;
      } else if (typeof a[key] == 'undefined') {
          return -1;
      } else if (typeof b[key] == 'undefined') {
          return 1;
      } else {
        if (a[key].toLowerCase() < b[key].toLowerCase()) return -1;
        else if (a[key].toLowerCase() > b[key].toLowerCase()) return 1;
        else return 0;
      }
    });

    // check to see if order changed at all
    // if order did not change, it's already sorted. Do descending instead
    let newOrder: String = JSON.stringify(this.users.map(item => item[key]));
    if (order == newOrder) {
      this.userSym = '▼';
      this.users.sort((a, b) => {
        if (typeof a[key] == 'number') {
          if (a[key] < b[key]) return 1;
          else if (a[key] > b[key]) return -1;
          else return 0;
        } else if (typeof a[key] == 'undefined') {
          return 1;
        } else if (typeof b[key] == 'undefined') {
          return -1;
        } else {
          if (a[key].toLowerCase() < b[key].toLowerCase()) return 1;
          else if (a[key].toLowerCase() > b[key].toLowerCase()) return -1;
          else return 0;
        }
      });
    } else {
      this.userSym = '▲'
    }
  }

  /**
   * Sets enrichLeads to true for the given user 
   * @param userId: User.objectId for the record to edit
   * @return the User object
   */
  enablePipl(userId: string): any {
      return this.aws.invokeLambda('LambdaAdminEnablePipl', { 'userId': userId }).then((result) => {
      if (result['success']) {
        let msg = result['message'] || 'Pipl has been enabled.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
      } else {
        let msg = result['message'] || 'Error getting user record. Please try again.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
      }
    });
  }

  /**
   * Get a single user from Users by cognitoId
   * @param cognitoId: matches cognito id in the CognitoIdToUser record
   * @return the User object
   */
  getUser(cognitoId: string): any {
      return this.aws.invokeLambda('LambdaAdminGetUser', { 'cognitoId': cognitoId }).then((result) => {
      if (result['success']) {
        return result['response']['userRecord'];
      } else {
        let msg = result['message'] || 'Error getting user record. Please try again.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
      }
    });
  }

  /**
   * Get a single user's notes from Notes by userId
   * @param userId: matches objectId in the User record
   * @return the Notes object
   */
  getNotes(userId: string): any {
      return this.aws.invokeLambda('LambdaAdminGetNotes', { 'userId': userId }).then((result) => {
      if (result['success']) {
        return result['response'];
      }else {
        let msg = result['message'] || 'Error getting notes. Please try again.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
      }
    });
  }

  /**
   * Get a single user's notes from Notes by userId
   * @param userId: matches objectId in the User record
   * @return the Notes object
   */
  updateNotes(userId: string, notes: string): any {
    return this.aws.invokeLambda('LambdaAdminUpdateNotes', { 'userId': userId, 'notes': notes }).then((result) => {
      if (result['success']) {
        return result['response'];
      }else {
        let msg = result['message'] || 'Error updating notes. Please try again.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
      }
    });
  }

  /**
   * Log in to the SherloQ dashboard as a specific user
   * @param userId: The user you want to log in as
   * @return success or error
   */
  imposterLogin(userId: string): any {
    return this.aws.invokeLambda('LambdaAdminImposterLogin', { 'userId': userId }).then((result) => {
      if (result['success']) {
        return result;
      }else {
        let msg = result['response'] || 'Error creating login. Please try again.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
      }
    });
  }

  /**
   * Archive a User record and disable the Cognito account
   * @param cognitoId: The User name value for the associated Cognito account
   * @param userRecord: The full User record
   * @return success or error
   */
  disableUser(cognitoId: string, userRecord: object): any {
    return this.aws.invokeLambda('LambdaAdminDisableUser', { 'cognitoId': cognitoId, 'userRecord': userRecord }).then((result) => {
      if (result['success']) {
        let msg = 'This user has been disabled.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
        return "success";
      }else {
        let msg = 'Error disabling user. Please try again.';
        let alert = this.alertCtrl.create({
          message: msg,
          buttons: ['Ok'],
          enableBackdropDismiss: false
        });
        alert.present();
      }
    });
  }

}



// WEBPACK FOOTER //
// ./src/providers/users/users.ts